import { ApiError } from "../lib/apiError";

export const MAX_UPLOAD_PIPELINES = 3;
export const MAX_UPLOAD_CONTROL_REQUESTS = 4;
export const MAX_UPLOAD_ATTEMPTS = 4;
export const UPLOAD_RETRY_BASE_MS = 1000;
export const UPLOAD_RETRY_MAX_MS = 60_000;
export const DATA_OBJECT_COUNTS_ACTIVE_INTERVAL_MS = 5000;
export const DATA_OBJECT_COUNTS_IDLE_INTERVAL_MS = 15_000;

export type UploadReceiptPhase =
  | "queued"
  | "retry_waiting"
  | "creating"
  | "uploading"
  | "finalizing"
  | "checking"
  | "refused"
  | "cancelled";

export interface UploadAggregate {
  total: number;
  waiting: number;
  waitingToRetry: number;
  transferring: number;
  checking: number;
  ready: number;
  refused: number;
  cancelled: number;
}

/** One O(n) pass over compact receipt metadata; file bytes never enter it. */
export function aggregateUploadItems(
  items: readonly { phase: UploadReceiptPhase }[],
): UploadAggregate {
  const counts: UploadAggregate = {
    total: items.length,
    waiting: 0,
    waitingToRetry: 0,
    transferring: 0,
    checking: 0,
    ready: 0,
    refused: 0,
    cancelled: 0,
  };
  for (const item of items) {
    switch (item.phase) {
      case "queued":
        counts.waiting += 1;
        break;
      case "retry_waiting":
        counts.waitingToRetry += 1;
        break;
      case "creating":
      case "uploading":
        counts.transferring += 1;
        break;
      case "finalizing":
      case "checking":
        counts.checking += 1;
        break;
      case "refused":
        counts.refused += 1;
        break;
      case "cancelled":
        counts.cancelled += 1;
        break;
    }
  }
  return counts;
}

/**
 * Merge the dialog's definitive transfer state with the batch-filtered server
 * totals. Browser-known waiting/transferring work takes precedence until its
 * request completes; terminal server counts classify the remaining rows.
 */
export function aggregateUploadReceipt(
  items: readonly { phase: UploadReceiptPhase; dataObjectId: string | null }[],
  serverCounts: { ready: number; refused: number } | null,
): UploadAggregate {
  const local = aggregateUploadItems(items);
  if (serverCounts === null) return local;

  const refusedWithoutRow = items.filter(
    (item) => item.phase === "refused" && item.dataObjectId === null,
  ).length;
  const refusedWithRow = local.refused - refusedWithoutRow;
  const classifiedCapacity = Math.max(
    0,
    local.total
      - local.waiting
      - local.waitingToRetry
      - local.transferring
      - local.cancelled
      - refusedWithoutRow,
  );
  const refusedFromRows = Math.min(
    classifiedCapacity,
    Math.max(serverCounts.refused, refusedWithRow),
  );
  const ready = Math.min(
    classifiedCapacity - refusedFromRows,
    serverCounts.ready,
  );

  return {
    total: local.total,
    waiting: local.waiting,
    waitingToRetry: local.waitingToRetry,
    transferring: local.transferring,
    checking: classifiedCapacity - refusedFromRows - ready,
    ready,
    refused: refusedWithoutRow + refusedFromRows,
    cancelled: local.cancelled,
  };
}

interface DataObjectCreateInput {
  projectId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  idempotencyKey: string;
  uploadBatchId: string;
}

/** Contract-shaped opaque id for one Add data dialog session. */
export function createUploadBatchId(
  randomUUID = globalThis.crypto.randomUUID(),
): string {
  const suffix = randomUUID.toLowerCase().replace(/[^a-z0-9]/gu, "");
  if (suffix.length === 0) throw new Error("Upload batch identity is unavailable");
  return `ub_${suffix}`;
}

/** Build every create attempt from the item's one stable upload identity. */
export function dataObjectCreateRequest(input: DataObjectCreateInput): RequestInit {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": input.idempotencyKey,
    },
    body: JSON.stringify({
      projectId: input.projectId,
      displayName: input.fileName,
      uploadBatchId: input.uploadBatchId,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
    }),
  };
}

interface QueueEntry {
  task: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}

export interface BoundedUploadQueue {
  enqueue<T>(task: () => Promise<T>): Promise<T>;
  readonly activeCount: number;
  readonly waitingCount: number;
}

/** A FIFO whose active count covers only the create -> transfer -> complete pipeline. */
export function createBoundedUploadQueue(limit = MAX_UPLOAD_PIPELINES): BoundedUploadQueue {
  if (!Number.isInteger(limit) || limit < 1) throw new Error("Upload queue limit must be positive");

  const waiting: QueueEntry[] = [];
  let active = 0;

  function drain(): void {
    while (active < limit) {
      const entry = waiting.shift();
      if (!entry) return;
      active += 1;
      void entry
        .task()
        .then((value) => {
          active -= 1;
          drain();
          entry.resolve(value);
        }, (error: unknown) => {
          active -= 1;
          drain();
          entry.reject(error);
        });
    }
  }

  return {
    enqueue<T>(task: () => Promise<T>) {
      const completion = new Promise<T>((resolve, reject) => {
        waiting.push({
          task,
          resolve: (value) => resolve(value as T),
          reject,
        });
      });
      drain();
      return completion;
    },
    get activeCount() {
      return active;
    },
    get waitingCount() {
      return waiting.length;
    },
  };
}

export class UploadTransferError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = "UploadTransferError";
    this.status = status;
  }
}

export function isTransientUploadFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.code === "concurrency_limit"
      || error.status === 429
      || (error.status >= 500 && error.status <= 599);
  }
  if (error instanceof UploadTransferError) {
    return error.status === null || (error.status >= 500 && error.status <= 599);
  }
  // fetch rejects with TypeError when no HTTP response was received.
  return error instanceof TypeError;
}

const ATTACHED_UPLOAD_PHASES: ReadonlySet<UploadReceiptPhase> = new Set([
  "queued",
  "retry_waiting",
  "creating",
  "uploading",
  "finalizing",
]);

/** Reopen an active transfer session; otherwise begin a distinct server batch. */
export function uploadBatchIdForDialogSession(
  items: readonly { phase: UploadReceiptPhase; uploadBatchId: string }[],
  mint: () => string = createUploadBatchId,
): string {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item && ATTACHED_UPLOAD_PHASES.has(item.phase)) return item.uploadBatchId;
  }
  return mint();
}

export function uploadRetryDelayMs(
  error: unknown,
  failedAttempt: number,
  random: () => number = Math.random,
): number {
  const exponent = Math.max(0, failedAttempt - 1);
  const backoffCeiling = Math.min(
    UPLOAD_RETRY_MAX_MS,
    UPLOAD_RETRY_BASE_MS * 2 ** exponent,
  );
  const jitter = Math.floor(backoffCeiling * Math.min(1, Math.max(0, random())));
  const retryAfter = error instanceof ApiError ? error.retryAfterSeconds : null;
  return retryAfter === null ? jitter : Math.max(jitter, retryAfter * 1000);
}

interface RetryOptions {
  attempts?: number;
  sleep?: (milliseconds: number) => Promise<void>;
  random?: () => number;
  /** Do not violate a containing operation's wall-clock recovery bound. */
  deadlineMs?: number;
  now?: () => number;
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/** Retry one request only for the card's closed transient set. */
export async function retryUploadOperation<T>(
  operation: (attempt: number) => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const attempts = options.attempts ?? MAX_UPLOAD_ATTEMPTS;
  const wait = options.sleep ?? sleep;
  const now = options.now ?? Date.now;
  if (attempts !== Number.POSITIVE_INFINITY && (!Number.isInteger(attempts) || attempts < 1)) {
    throw new Error("Retry attempts must be positive");
  }

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await operation(attempt);
    } catch (error) {
      if (attempt === attempts || !isTransientUploadFailure(error)) throw error;
      const delayMs = uploadRetryDelayMs(error, attempt, options.random);
      // Giving up is preferable to retrying earlier than Retry-After or
      // letting one response extend a separately documented recovery window.
      if (options.deadlineMs !== undefined && now() + delayMs > options.deadlineMs) throw error;
      await wait(delayMs);
    }
  }

  throw new Error("Upload retry loop ended without a result");
}

/** A spent transient budget stays explicit and resumable; a definitive answer refuses. */
export function uploadFailurePhase(error: unknown): "retry_waiting" | "refused" {
  return uploadFailureReceipt(error).retryable ? "retry_waiting" : "refused";
}

const ERROR_REASON: Readonly<Record<string, string>> = {
  admission_not_ready: "The file was not ready to be checked. Try again.",
  concurrency_limit: "The service is busy receiving other files. Try again.",
  database_unavailable: "The service is busy receiving other files. Try again.",
  data_objects_unavailable: "File uploads are not available right now. Try again later.",
  finalization_in_progress: "The file is still being received. Try again.",
  forbidden: "You are not allowed to add files to this project.",
  idempotency_conflict: "This upload no longer matches this file. Add the file again.",
  idempotency_in_progress: "The file is still being received. Try again.",
  invalid_request: "This file could not be received because its details were not valid.",
  not_found: "This project or upload is no longer available.",
  unauthorized: "Your session has ended. Sign in again.",
  upload_expired: "The upload took too long. Try again.",
  upload_missing: "The uploaded file could not be found. Add it again.",
  upload_size_mismatch: "The uploaded file did not match its declared size or format.",
};

export interface UploadFailureReceipt {
  reason: string;
  requestId: string | null;
  retryable: boolean;
}

function serviceReceiveFailure(requestId: string | null): string {
  const request = requestId ?? "not provided";
  return `The service could not receive this file (request ${request}). Try again.`;
}

/** Turn API/storage failures into customer copy; raw messages and codes never reach the receipt. */
export function uploadFailureReceipt(error: unknown): UploadFailureReceipt {
  if (error instanceof ApiError) {
    const reason =
      error.status === 500 || error.code === "internal"
        ? serviceReceiveFailure(error.requestId)
        : error.code === null
          ? serviceReceiveFailure(error.requestId)
          : (ERROR_REASON[error.code] ?? "The service could not receive this file. Try again.");
    return {
      reason,
      requestId: error.requestId,
      retryable:
        isTransientUploadFailure(error) ||
        error.code === "data_objects_unavailable",
    };
  }
  if (error instanceof UploadTransferError) {
    return {
      reason:
        error.status === 403
          ? "The upload service refused this file transfer. Add the file again."
          : error.status === null
            ? "The connection dropped while this file was uploading. Try again."
            : "The file transfer was refused. Try again.",
      requestId: null,
      retryable: isTransientUploadFailure(error),
    };
  }
  if (error instanceof TypeError) {
    return {
      reason: "The connection dropped while this file was being received. Try again.",
      requestId: null,
      retryable: true,
    };
  }
  return {
    reason: "The service could not receive this file. Try again.",
    requestId: null,
    retryable: false,
  };
}

interface DataObjectCountsSnapshot {
  total: number;
  ready: number;
  checking: number;
  refused: number;
}

function sameCounts(
  previous: DataObjectCountsSnapshot | null,
  current: DataObjectCountsSnapshot,
): boolean {
  return previous !== null
    && previous.total === current.total
    && previous.ready === current.ready
    && previous.checking === current.checking
    && previous.refused === current.refused;
}

/** Active batches refresh at 5 s; an unchanged aggregate backs off to 15 s. */
export function dataObjectCountsPollIntervalMs(
  previous: DataObjectCountsSnapshot | null,
  current: DataObjectCountsSnapshot,
): 5000 | 15000 {
  return sameCounts(previous, current)
    ? DATA_OBJECT_COUNTS_IDLE_INTERVAL_MS
    : DATA_OBJECT_COUNTS_ACTIVE_INTERVAL_MS;
}
